import { useEffect, useState } from "react";

import type {
    ActivationAlertsContractSocialMediaPost,
    ActivationAlertsSocialMediaGroupPostsDetailParams,
} from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";

import { useDurationContext } from "../../../../contexts/DurationContext";
import { useAlertsParams } from "../../../../hooks/useAlertsParams";

interface IUseLazySocialPostsParams
    extends Omit<ActivationAlertsSocialMediaGroupPostsDetailParams, "activationId" | "startDate" | "endDate"> {
    initialPosts: ActivationAlertsContractSocialMediaPost[];
    enabled: boolean;
    shouldTriggerFetch: boolean;
}

export const useLazySocialPosts = ({
    initialPosts,
    enabled,
    shouldTriggerFetch,
    ...params
}: IUseLazySocialPostsParams) => {
    const { activationId } = useAlertsParams();
    const duration = useDurationContext();

    const [isFetchAllTriggered, setIsFetchAllTriggered] = useState(false);

    const { data, isPlaceholderData, isLoading, isError } = useApiQuery(
        {
            tag: "Alerts",
            methodName: "activationAlertsSocialMediaGroupPostsDetail",
            options: {
                placeholderData: initialPosts,
                enabled: enabled && isFetchAllTriggered,
            },
        },
        {
            ...params,
            activationId,
            endDate: duration.forAPI.iso.to,
            startDate: duration.forAPI.iso.from,
        },
    );

    useEffect(() => {
        if (enabled && shouldTriggerFetch && !isLoading && isPlaceholderData) {
            setIsFetchAllTriggered(true);
        }
    }, [enabled, shouldTriggerFetch, isLoading, isPlaceholderData]);

    const posts = !enabled || isError ? initialPosts : data;

    return {
        posts,
        isError,
        isLoading,
        isPlaceholderData,
    };
};
